import ReactECharts from 'echarts-for-react';

/**
 * BotCareerPathChart — autonomy rises and human review drops as a bot earns
 * trust through its lifecycle (blog03).
 *
 * Chart-type-first: this is a two-series relationship over a categorical
 * lifecycle → best-fit engine is a line/area chart. ECharts native line.
 * Brand colors: review = warning amber, autonomy = success green, Raisy teal accents.
 * Opaque white background.
 */

const BRAND = {
  teal: '#0AB3BF',
  tealDark: '#087B8B',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  slate: '#334155',
  muted: '#64748B',
  surface: '#FFFFFF',
};

const STAGES = ['Hired', 'Probation', 'Reviewed', 'Semi-Auto', 'Full-Auto'];
// Autonomy (what the bot does on its own) and Review (what a human still gates).
const AUTONOMY = [0, 10, 30, 60, 90];
const REVIEW = [100, 90, 70, 40, 10];

/** ONE shared percentage formatter — tooltip, axis ticks, and point labels all use it. */
const formatPct = (v: number) => `${Math.round(v)}%`;

export const BotCareerPathChart = ({ height = 340 }: { height?: number }) => {
  const option = {
    backgroundColor: BRAND.surface,
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#1E293B',
      borderColor: BRAND.slate,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (params: { seriesName: string; value: number; dataIndex: number; marker?: string }[]) => {
        const i = params[0].dataIndex;
        return `${STAGES[i]}<br/>${params
          .map((p) => `${p.marker} ${p.seriesName}: <b>${formatPct(p.value)}</b>`)
          .join('<br/>')}`;
      },
    },
    legend: {
      top: 8,
      textStyle: { color: BRAND.slate, fontSize: 13 },
      data: ['Autonomy (bot acts)', 'Human review (gates)'],
    },
    grid: { left: 48, right: 40, top: 48, bottom: 40 },
    xAxis: {
      type: 'category',
      data: STAGES,
      axisLine: { lineStyle: { color: BRAND.slate } },
      axisLabel: { color: BRAND.muted, fontSize: 13, fontWeight: 600 },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      name: 'share of actions (%)',
      nameTextStyle: { color: BRAND.muted, fontSize: 11 },
      axisLabel: { color: BRAND.muted, fontSize: 12, formatter: (v: number) => formatPct(v) },
      splitLine: { lineStyle: { color: BRAND.slate, opacity: 0.35 } },
    },
    series: [
      {
        name: 'Autonomy (bot acts)',
        type: 'line',
        smooth: true,
        symbolSize: 9,
        data: AUTONOMY,
        itemStyle: { color: BRAND.success },
        lineStyle: { color: BRAND.success, width: 3 },
        areaStyle: { color: BRAND.success, opacity: 0.12 },
        label: {
          show: true,
          position: 'top',
          color: BRAND.success,
          fontWeight: 700,
          fontSize: 12,
          formatter: (p: { value: number }) => formatPct(p.value),
        },
      },
      {
        name: 'Human review (gates)',
        type: 'line',
        smooth: true,
        symbolSize: 9,
        data: REVIEW,
        itemStyle: { color: BRAND.warning },
        lineStyle: { color: BRAND.warning, width: 3, type: 'dashed' },
        areaStyle: { color: BRAND.warning, opacity: 0.08 },
        label: {
          show: true,
          position: 'bottom',
          color: BRAND.warning,
          fontWeight: 700,
          fontSize: 12,
          formatter: (p: { value: number }) => formatPct(p.value),
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 text-center">
        Figure 1. Autonomy is earned: bot autonomy rises from 0% at hire to 90% at full-auto while human review falls
        from 100% to 10% — review scales with trust but never disappears. Hover for values.
      </p>
    </div>
  );
};
